import { getRiskBandLabel } from '../../utils/bands';
import { measuredRiskScore } from './measuredRiskScore';

type PortfolioCompany = {
  company_name?: string;
  name?: string;
  industry?: string | null;
  risk_score?: number | string | null;
  annual_revenue?: number | string | null;
  revenue_at_risk?: number | null;
  revenue_at_risk_calculation?: { annual_revenue?: number | null } | null;
  recovery_days?: number | null;
};

const num = (v: unknown): number | null => {
  if (v === null || v === undefined || v === '') return null;
  const n = typeof v === 'number' ? v : Number(v);
  return Number.isFinite(n) ? n : null;
};

const sizeOf = (revenue: number | null): string => {
  if (revenue === null) return 'Not recorded';
  if (revenue >= 1_000_000_000) return 'Enterprise';
  if (revenue >= 100_000_000) return 'Large';
  if (revenue >= 10_000_000) return 'Medium';
  return 'Small';
};

/**
 * The Financial tab's figures, built only from each client's backend business intelligence
 * (revenue_at_risk, recovery_days — the same computation as the client's Compass) and its CRS.
 * A client without backend figures is left out; nothing is estimated here.
 */
export function portfolioFinancials(companies: PortfolioCompany[], normalizeIndustry: (i?: string | null) => string = (i) => i || 'Other') {
  const included = (companies || []).filter((c) => num(c.revenue_at_risk) !== null && num(c.recovery_days) !== null);

  let total = 0;
  let totalRevenue = 0;
  let revenueCount = 0;
  let atRiskWithRevenue = 0; // the percentage pairs each client's at-risk dollars with its own revenue
  const byCategory: Record<string, { revenue: number; atRisk: number; companies: number }> = {};
  const byIndustry: Record<string, { exposure: number; companies: number; avgRisk: number | null; totalRevenue: number | null; scored: number; riskSum: number }> = {};
  const bySize: Record<string, { exposure: number; companies: number; avgRevenue: number; revenueSum: number }> = {};
  const recoveryTimeData = included.map((c) => {
    const revenueAtRisk = num(c.revenue_at_risk) as number;
    const recoveryDays = num(c.recovery_days) as number;
    // The revenue the BI calculation used (Q11, else the company profile), so the at-risk dollars and
    // the revenue they are a share of come from one source. The list's own annual_revenue is Q11 only.
    const annualRevenue = num(c.revenue_at_risk_calculation?.annual_revenue) ?? num(c.annual_revenue);
    const riskScore = measuredRiskScore(c);
    const category = riskScore === null ? 'Not scored' : getRiskBandLabel(riskScore);
    const industry = normalizeIndustry(c.industry);

    total += revenueAtRisk;
    if (annualRevenue !== null) { // a missing revenue is skipped, never counted as $0
      totalRevenue += annualRevenue;
      revenueCount += 1;
      atRiskWithRevenue += revenueAtRisk;
    }

    byCategory[category] ??= { revenue: 0, atRisk: 0, companies: 0 };
    if (annualRevenue !== null) byCategory[category].revenue += annualRevenue;
    byCategory[category].atRisk += revenueAtRisk;
    byCategory[category].companies += 1;

    byIndustry[industry] ??= { exposure: 0, companies: 0, avgRisk: null, totalRevenue: null, scored: 0, riskSum: 0 };
    byIndustry[industry].exposure += revenueAtRisk;
    byIndustry[industry].companies += 1;
    if (annualRevenue !== null) byIndustry[industry].totalRevenue = (byIndustry[industry].totalRevenue ?? 0) + annualRevenue;
    if (riskScore !== null) { byIndustry[industry].scored += 1; byIndustry[industry].riskSum += riskScore; }

    const size = sizeOf(annualRevenue);
    bySize[size] ??= { exposure: 0, companies: 0, avgRevenue: 0, revenueSum: 0 };
    bySize[size].exposure += revenueAtRisk;
    bySize[size].companies += 1;
    if (annualRevenue !== null) bySize[size].revenueSum += annualRevenue;

    return {
      company: c.company_name || c.name || 'Unknown',
      industry,
      riskScore,
      category,
      recoveryDays,
      revenueAtRisk,
      annualRevenue,
      dailyRevenue: annualRevenue === null ? null : annualRevenue / 365,
    };
  });

  for (const d of Object.values(byIndustry)) d.avgRisk = d.scored > 0 ? d.riskSum / d.scored : null;
  for (const d of Object.values(bySize)) d.avgRevenue = d.companies > 0 ? d.revenueSum / d.companies : 0;

  return {
    includedCount: included.length,
    revenueAtRisk: {
      // null, not $0, when no client has figures: shown as "Not measured".
      total: included.length > 0 ? total : null,
      totalRevenue: revenueCount > 0 ? totalRevenue : null,
      percentage: totalRevenue > 0 ? (atRiskWithRevenue / totalRevenue) * 100 : null,
      byCategory,
      recoveryTimeData: recoveryTimeData.sort((a, b) => b.revenueAtRisk - a.revenueAtRisk).slice(0, 10),
    },
    exposure: { total: included.length > 0 ? total : null, byIndustry, bySize },
  };
}
